import React, { useEffect, useRef, useState } from 'react';
import { FiChevronDown, FiGlobe } from 'react-icons/fi';
import { useLanguage } from '../contexts/LanguageContext';

function LanguageSwitcher({ compact = false, className = '', onLanguageChange }) {
  const { changeLanguage, language, supportedLanguages, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState('');
  const rootRef = useRef(null);
  const switcherClassName = ['js-language-switcher', compact ? 'is-compact' : '', className].filter(Boolean).join(' ');
  const activeLanguage = supportedLanguages.find((option) => option.code === language) || supportedLanguages[0];

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, []);

  const selectLanguage = async (nextLanguage) => {
    if (busy) {
      setOpen(false);
      return;
    }

    if (nextLanguage === language) {
      setOpen(false);
      await onLanguageChange?.(nextLanguage);
      return;
    }
    const previousLanguage = language;
    setBusy(nextLanguage);
    changeLanguage(nextLanguage);
    setOpen(false);
    try {
      await onLanguageChange?.(nextLanguage);
    } catch (error) {
      changeLanguage(previousLanguage);
    } finally {
      setBusy('');
    }
  };

  return (
    <div className={switcherClassName} ref={rootRef}>
      {!compact && <span className="js-language-switcher__label">{t('common.language')}</span>}

      <div className="js-language-switcher__dropdown">
        <button
          type="button"
          className={`js-language-switcher__trigger ${open ? 'is-open' : ''}`}
          onClick={() => setOpen((current) => !current)}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-label={t('common.language')}
        >
          <FiGlobe className="js-language-switcher__globe" aria-hidden="true" />
          <span className="js-language-switcher__trigger-copy">
            <strong>{compact ? (language === 'ar' ? 'ع' : activeLanguage.label) : t('common.language')}</strong>
            <small>{activeLanguage.fullLabel}</small>
          </span>
          <FiChevronDown className={`js-language-switcher__chevron ${open ? 'is-open' : ''}`} />
        </button>

        {open && (
          <div className="js-language-switcher__menu" role="listbox" aria-label={t('common.language')}>
            {supportedLanguages.map((option) => (
              <button
                key={option.code}
                type="button"
                role="option"
                className={`js-language-switcher__option ${language === option.code ? 'is-active' : ''}`}
                onClick={() => selectLanguage(option.code)}
                disabled={Boolean(busy)}
                aria-selected={language === option.code}
              >
                <span>{option.fullLabel}</span>
                <small>{busy === option.code ? '…' : option.code === 'ar' ? 'ع' : option.label}</small>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default LanguageSwitcher;
